"use client"

import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"

import { cn } from "@/lib/utils"
import { useThemeConfig } from "@/hooks/use-theme-config"

const Popover = PopoverPrimitive.Root

const PopoverTrigger = PopoverPrimitive.Trigger

const PopoverContent = ({
  className,
  align = "center",
  sideOffset = 4,
  ref,
  ...props
}: React.ComponentPropsWithRef<typeof PopoverPrimitive.Content>) => {
  const { isTerminal, hasGlow } = useThemeConfig()

  // Hydration-safe pattern: only show terminal styling after mount
  const mounted = React.useSyncExternalStore(
    () => () => {},
    () => true,
    () => false
  )

  // Use safe values during SSR
  const showTerminal = mounted && isTerminal
  const showGlow = mounted && hasGlow

  const handleMouseDown = React.useCallback((e: React.MouseEvent) => {
    // Prevent event from bubbling to parent modals
    e.stopPropagation();
  }, []);

  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          // Base styles (non-terminal)
          showTerminal
            ? "z-[1000] w-72 rounded-md p-4 outline-none"
            : "z-[1000] w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          // Terminal mode: Bloomberg-style with emerald colors (no animations)
          showTerminal && [
            "bg-black border border-emerald-500",
            "text-emerald-400",
            "font-mono",
            // Static glow shadow (no animation)
            showGlow && "shadow-[0_0_15px_rgba(16,185,129,0.4),0_0_30px_rgba(16,185,129,0.2)]",
          ],
          className
        )}
        onMouseDown={handleMouseDown}
        onKeyDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
}
PopoverContent.displayName = PopoverPrimitive.Content.displayName

export { Popover, PopoverTrigger, PopoverContent }
